import React, { useState, useEffect } from 'react';
import { Box, Container, Card, TextField, Button, CircularProgress, Typography, FormControlLabel, Checkbox, Alert, Snackbar, Grid, Paper } from '@mui/material';
import { subAdminService } from '../services/api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

const SubAdminFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    is_active: true,
    permissions: {
      campaigns: false,
      campaign_submissions: false,
      campaign_categories: false,
      ratings: false,
      users: false,
      volunteers: false,
      donations: false,
      gallery: false,
      home_slider: false,
      home_popup: false,
      site_stats: false,
      testimonials: false,
      newsletters: false,
      legal_pages: false,
      financial_documents: false,
      founders: false,
      team_members: false,
      contacts: false,
      reports: false,
    }
  });

  const [errors, setErrors] = useState({});

  // Load sub-admin data if editing
  useEffect(() => {
    if (isEditMode) {
      loadSubAdmin();
    }
  }, [id]);

  const loadSubAdmin = async () => {
    try {
      setLoading(true);
      const response = await subAdminService.getSubAdminById(id);
      const data = response.data?.data || response.data;
      setFormData({
        username: data.username || '',
        email: data.email || '',
        password: '',
        confirmPassword: '',
        is_active: data.is_active !== false,
        permissions: {
          campaigns: false,
          campaign_submissions: false,
          campaign_categories: false,
          ratings: false,
          users: false,
          volunteers: false,
          donations: false,
          gallery: false,
          home_slider: false,
          home_popup: false,
          site_stats: false,
          testimonials: false,
          newsletters: false,
          legal_pages: false,
          financial_documents: false,
          founders: false,
          team_members: false,
          contacts: false,
          reports: false,
          ...(data.permissions || {}),
        }
      });
    } catch (error) {
      console.error('Error loading sub-admin:', error);
      setToast({ open: true, message: 'Error loading sub-admin', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!isEditMode && !formData.password) {
      newErrors.password = 'Password is required';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!Object.values(formData.permissions).some(p => p)) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionChange = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission]
      }
    }));
    if (errors.permissions) {
      setErrors(prev => ({
        ...prev,
        permissions: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        username: formData.username,
        email: formData.email,
        is_active: formData.is_active,
        permissions: formData.permissions
      };

      if (formData.password) {
        submitData.password = formData.password;
      }

      if (isEditMode) {
        await subAdminService.updateSubAdmin(id, submitData);
        setToast({ open: true, message: 'Sub-admin updated successfully!', type: 'success' });
      } else {
        await subAdminService.createSubAdmin(submitData);
        setToast({ open: true, message: 'Sub-admin created successfully!', type: 'success' });
      }

      setTimeout(() => {
        navigate('/admin/sub-admins');
      }, 1500);
    } catch (error) {
      console.error('Error saving sub-admin:', error);
      setToast({ 
        open: true, 
        message: 'Error: ' + (error.response?.data?.error || error.message), 
        type: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const permissionLabels = {
    campaigns: 'Campaigns Management',
    campaign_submissions: 'Campaign Submissions (approve user campaigns)',
    campaign_categories: 'Campaign Categories',
    ratings: 'Campaign Ratings (approve user reviews)',
    users: 'Users Management',
    volunteers: 'Volunteers Management',
    donations: 'Donations Management',
    gallery: 'Gallery Management',
    home_slider: 'Home Page Slider',
    home_popup: 'Home Page Popup',
    site_stats: 'Site Stats (Join With Us counters)',
    testimonials: 'Testimonials (What People Say)',
    newsletters: 'Newsletter Subscribers',
    legal_pages: 'Legal Pages (Terms, Privacy, Refund)',
    financial_documents: 'Financial & Legal Documents',
    founders: 'Founders Management',
    team_members: 'Team Members Management',
    contacts: 'Contacts Management',
    reports: 'Reports & Analytics'
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/sub-admins')}
            sx={{ color: '#2c5f2d', mb: 2 }}
          >
            Back to Sub Admins
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            {isEditMode ? 'Edit Sub Admin' : 'Add New Sub Admin'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            {isEditMode ? 'Update sub-admin details and permissions' : 'Create a new sub-admin with specific permissions'}
          </Typography>
        </Box>

        <Card sx={{ p: 4, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
              Basic Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  error={!!errors.username}
                  helperText={errors.username}
                  variant="outlined"
                  size="small"
                  placeholder="Enter username"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="outlined"
                  size="small"
                  placeholder="Enter email"
                />
              </Grid>

              {!isEditMode && (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.is_active}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                    }
                    label="Active Account"
                  />
                </Grid>
              )}
            </Grid>

            {/* Password Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
              {isEditMode ? 'Change Password (Optional)' : 'Password'}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password || (isEditMode ? 'Leave blank to keep current password' : 'Minimum 8 characters')}
                  variant="outlined"
                  size="small"
                  placeholder="Enter password"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  variant="outlined"
                  size="small"
                  placeholder="Confirm password"
                />
              </Grid>
            </Grid>

            {/* Permissions Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
              Module Permissions
            </Typography>

            {errors.permissions && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.permissions}
              </Alert>
            )}

            <Paper sx={{ p: 3, backgroundColor: '#f9fafb', border: '1px solid #e8ecf1', mb: 4 }}>
              <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
                Select which modules this sub-admin can access and manage:
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(permissionLabels)
                  .filter(([key]) => key !== 'testimonials')
                  .map(([key, label]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.permissions[key]}
                          onChange={() => handlePermissionChange(key)}
                          sx={{
                            color: '#2c5f2d',
                            '&.Mui-checked': {
                              color: '#2c5f2d',
                            }
                          }}
                        />
                      }
                      label={label}
                      sx={{ '& .MuiTypography-root': { fontSize: '0.95rem' } }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Permissions Summary */}
            {Object.values(formData.permissions).some(p => p) && (
              <Alert severity="info" sx={{ mb: 4 }}>
                <Typography variant="body2">
                  <strong>Permissions Summary:</strong> This sub-admin will have access to {Object.values(formData.permissions).filter(p => p).length} module{Object.values(formData.permissions).filter(p => p).length !== 1 ? 's' : ''}.
                </Typography>
              </Alert>
            )}

            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/sub-admins')}
                sx={{
                  borderColor: '#d5d9e2',
                  color: '#667085',
                  '&:hover': { borderColor: '#b0b8c1', backgroundColor: '#f5f7fa' }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={saving}
                sx={{
                  backgroundColor: '#2c5f2d',
                  '&:hover': { backgroundColor: '#1f4620' },
                  '&:disabled': { backgroundColor: '#cccccc' }
                }}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                    Saving...
                  </>
                ) : (
                  isEditMode ? 'Update Sub Admin' : 'Create Sub Admin'
                )}
              </Button>
            </Box>
          </form>
        </Card>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubAdminFormPage;
